import { type ComponentProps, FC, type JSX } from 'react';

import { CircledNumber } from '@eneco/components/ui/CircledNumber/CircledNumber';
import { useFormatter } from '@eneco/i18n';
import {
  Badge,
  Box,
  Card,
  Grid,
  Heading,
  IconLoader,
  Image,
  Invisible,
  Ribbon as SparkyRibbon,
  Stack,
  Stretch,
  Text,
  VisuallyHidden,
} from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { useI18nTranslation } from '@eneco/sparky/providers';
import { Placeholder, mapImage, wrap } from '@sitecore/common';
import { ContentCardRendering } from '@sitecore/types/manual/ContentCard';

import { ConditionalWrapper } from './ConditionalWrapper';
import { ContentCardBodyText } from './ContentCardBodyText';
import { ContentCardButtonLink, ContentCardTextLink } from './ContentCardCtas';

interface Props {
  showRibbonPlaceholder?: boolean;
  variant?: 'default' | 'article';
}

const Ribbon: FC<{ icon?: string; text: string }> = ({ icon, text }) => {
  return (
    <SparkyRibbon>
      <Stack direction="row" alignY="center" gap="2" alignX="center">
        {icon && (
          <Stack.Item>
            <IconLoader iconName={icon} color="iconInverted" size="small" />
          </Stack.Item>
        )}
        <Stack.Item>
          <Text display="inline">{text}</Text>
        </Stack.Item>
      </Stack>
    </SparkyRibbon>
  );
};

const wrapCompactCard = (children: JSX.Element) => <Box padding="6">{children}</Box>;

type ElevatedCardWrapperOptions = {
  showRibbonPlaceholder: boolean;
  isCompactCard: boolean;
  hasBackgroundColor: boolean;
  backgroundColor: ComponentProps<typeof Box>['backgroundColor'];
  ribbonText?: string;
  ribbonIcon?: string;
};

const createElevatedCardWrapper =
  ({
    showRibbonPlaceholder,
    isCompactCard,
    hasBackgroundColor,
    backgroundColor,
    ribbonText,
    ribbonIcon,
  }: ElevatedCardWrapperOptions) =>
  (children: JSX.Element) => (
    <>
      {showRibbonPlaceholder && (
        <Invisible>
          <SparkyRibbon>Invisible placeholder</SparkyRibbon>
        </Invisible>
      )}
      <Stretch>
        <Card overflow="hidden">
          {ribbonText && <Ribbon icon={ribbonIcon} text={ribbonText} />}
          <Stretch>
            <Box backgroundColor={hasBackgroundColor ? backgroundColor : 'backgroundPrimary'}>
              <ConditionalWrapper condition={isCompactCard} wrapper={wrapCompactCard}>
                {children}
              </ConditionalWrapper>
            </Box>
          </Stretch>
        </Card>
      </Stretch>
    </>
  );

const ContentCard: FC<ContentCardRendering & Props> = ({
  fields,
  params,
  showRibbonPlaceholder = false,
  variant = 'default',
}) => {
  const isDesktop = useMediaQuery('md');
  const step = useI18nTranslation('step');
  const { format } = useFormatter();

  const { title, content, badgeText, image, buttonLink, cardNumber, link, ribbonText, ribbonIcon } = fields;
  // Have to check for "1", as Sitecore's default behaviour is returning this as a string "1" or "0" instead of a boolean
  // Todo: Update this check when the response is updated to a boolean in Sitecore.
  const isElevated = params?.setElevation === '1';
  const isCompactOnMobile = params?.mobileVariant === 'compact';
  const isCompactOnDesktop = params?.desktopVariant === 'compact';
  const isCompactCard = (isDesktop && isCompactOnDesktop) || (!isDesktop && isCompactOnMobile);

  const hasBackgroundColor = Boolean(params?.backgroundColor);
  const headingLevel = params?.headingLevel || 'h3';

  const imageProps = mapImage(image);
  const hasImage = !!imageProps.src;

  return (
    <Stack>
      <ConditionalWrapper
        condition={isElevated}
        wrapper={createElevatedCardWrapper({
          showRibbonPlaceholder,
          isCompactCard,
          hasBackgroundColor,
          backgroundColor: params?.backgroundColor,
          ribbonText: ribbonText?.value,
          ribbonIcon: ribbonIcon?.value,
        })}>
        <Stretch>
          <Stack
            direction={isCompactCard ? 'row' : 'column'}
            gap={{ initial: isCompactCard ? '3' : '0', md: isCompactCard ? '6' : '0' }}>
            {imageProps.src && (
              <Stack.Item shrink={false}>
                <Box
                  paddingX={!isCompactCard && isElevated && variant === 'article' ? '6' : '0'}
                  paddingTop={!isCompactCard && isElevated && variant === 'article' ? '6' : '0'}>
                  <Grid gridTemplateColumns="40px auto 1fr" gridTemplateRows="20px auto 1fr">
                    <Grid.Item gridColumn="1 / -1" gridRow="1 / -1">
                      <Image
                        alt={imageProps.alt}
                        width={{
                          initial: isCompactOnMobile ? '80px' : '100%',
                          md: isCompactOnDesktop ? '160px' : '100%',
                        }}
                        height={{
                          initial: isCompactOnMobile ? '80px' : 'auto',
                          md: isCompactOnDesktop ? '160px' : 'auto',
                        }}
                        src={imageProps.src}
                        srcSet={imageProps.srcSet ?? ''}
                        sizes={{
                          initial: isCompactOnMobile ? '80px' : '100%',
                          md: isCompactOnDesktop ? '160px' : '100%',
                        }}
                        objectFit={params?.objectFit ?? 'cover'}
                        aspectRatio={variant === 'article' ? '1/1' : '16/9'}
                      />
                    </Grid.Item>
                    {cardNumber.value && !isCompactCard && (
                      <Grid.Item aria-hidden="true" gridColumn="2 / 2" gridRow="2 / 2">
                        <CircledNumber>{wrap(cardNumber)}</CircledNumber>
                      </Grid.Item>
                    )}
                    <Grid.Item gridColumn="1 / -1" gridRow="1 / -1">
                      <Placeholder name="jss-promo-box" />
                    </Grid.Item>
                  </Grid>
                </Box>
              </Stack.Item>
            )}
            {/* Check if any of the following fields have valid content before rendering the relevant section, This prevents rendering empty html tags. */}
            {(title?.value ||
              badgeText?.editable ||
              badgeText?.value ||
              content?.value ||
              (buttonLink?.value.href && buttonLink.value.text) ||
              buttonLink?.editable ||
              (link?.value.href && link.value.text) ||
              link?.editable) && (
              <Stretch>
                <Box
                  paddingX={isElevated && !isCompactCard ? '6' : '0'}
                  paddingTop={(isElevated || hasImage) && !isCompactCard ? '6' : '0'}
                  paddingBottom={isElevated && !isCompactCard ? '6' : '0'}>
                  <Stretch>
                    <Stack gap={isCompactCard ? '2' : '6'}>
                      {(title?.value || badgeText?.editable || badgeText?.value) && (
                        <Stack.Item>
                          <Stack gap={isCompactCard ? '1' : '2'}>
                            {title?.value && (
                              <Heading as={headingLevel} size={{ initial: 'XS', md: 'S' }}>
                                {cardNumber.value && (
                                  <VisuallyHidden>{format(step, { step: cardNumber.value })}: </VisuallyHidden>
                                )}
                                {wrap(title)}
                              </Heading>
                            )}
                            {badgeText?.editable || badgeText?.value ? (
                              <Stack.Item>
                                <Badge size="M">{wrap(badgeText)}</Badge>
                              </Stack.Item>
                            ) : null}
                          </Stack>
                        </Stack.Item>
                      )}

                      <Stack.Item grow>
                        <Stack gap={isCompactCard ? '2' : '6'}>
                          <ContentCardBodyText content={content} />
                          <Placeholder name="jss-content-card-main" />
                        </Stack>
                      </Stack.Item>

                      <ContentCardButtonLink buttonLink={buttonLink} />
                      <ContentCardTextLink link={link} hasBackgroundColor={hasBackgroundColor} />
                    </Stack>
                  </Stretch>
                </Box>
              </Stretch>
            )}
          </Stack>
        </Stretch>
      </ConditionalWrapper>
    </Stack>
  );
};

export { ContentCard };
export default ContentCard;
